import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    // Sende die Daten im Hintergrund an Netlify Form Handling
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="grid lg:grid-cols-5">
            
            {/* Contact Info */}
            <div className="lg:col-span-2 bg-slate-900 text-white p-10 lg:p-12">
              <h3 className="text-3xl font-bold mb-8">Kontaktieren Sie uns</h3>
              <p className="text-slate-300 mb-10">
                Haben Sie Fragen zu unseren Leistungen oder planen Sie ein Projekt? Wir stehen Ihnen gerne zur Verfügung.
              </p>
              
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/10 p-3 rounded-lg">
                    <SafeIcon name="MapPin" className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Adresse</p>
                    <p className="text-lg font-semibold mt-1">MK Netzbau GmbH</p>
                    <p className="text-slate-300">Burger Hauptstraße 14B<br/>35745 Herborn</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/10 p-3 rounded-lg">
                    <SafeIcon name="Phone" className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">Telefon</p>
                    <a href="tel:+4917622787128" className="text-lg font-semibold mt-1 hover:text-amber-500 transition-colors block">
                      +49 176 2278 71 28
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-white/10 p-3 rounded-lg">
                    <SafeIcon name="Mail" className="h-6 w-6 text-amber-500" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">E-Mail</p>
                    <a href="mailto:info@mk-netzbau.de" className="text-lg font-semibold mt-1 hover:text-amber-500 transition-colors block">
                      info@mk-netzbau.de
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3 p-10 lg:p-12 bg-white">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Schreiben Sie uns</h3>
              
              {status === 'success' ? (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-8 text-center h-full flex flex-col justify-center items-center pb-12"
                >
                  <SafeIcon name="CheckCircle" className="h-16 w-16 text-green-500 mb-4" />
                  <h4 className="text-xl font-bold mb-3">Vielen Dank für Ihre Nachricht!</h4>
                  <p className="text-slate-600 mb-6">Wir haben Ihre Anfrage erfolgreich erhalten und werden uns in Kürze bei Ihnen melden.</p>
                  <button 
                    onClick={() => setStatus('')}
                    className="mt-2 text-sm font-semibold text-amber-600 hover:text-amber-700 underline"
                  >
                    Weitere Nachricht senden
                  </button>
                </motion.div>
              ) : (
                <form 
                  name="contact" 
                  method="POST" 
                  data-netlify="true" 
                  netlify-honeypot="bot-field" 
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >
                  {/* Verstecktes Input-Feld, das Netlify zwingend für React-Formulare benötigt */}
                  <input type="hidden" name="form-name" value="contact" />
                  
                  {/* Honeypot-Feld gegen Spam-Bots */}
                  <p className="hidden">
                    <label>
                      Don’t fill this out if you're human: <input name="bot-field" />
                    </label>
                  </p>

                  {status === 'error' && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-md text-sm border border-red-200">
                      Es gab leider ein Problem beim Senden. Bitte versuchen Sie es später erneut.
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700">Name</label>
                      <input type="text" name="name" id="name" required className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm p-3 border" placeholder="Ihr Name" />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700">Unternehmen</label>
                      <input type="text" name="company" id="company" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm p-3 border" placeholder="Ihr Unternehmen" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700">E-Mail</label>
                    <input type="email" name="email" id="email" required className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm p-3 border" placeholder="ihre.email@beispiel.de" />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700">Nachricht</label>
                    <textarea name="message" id="message" required rows={4} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 sm:text-sm p-3 border" placeholder="Wie können wir Ihnen helfen?"></textarea>
                  </div>
                  <div>
                    <button type="submit" className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-bold text-slate-900 bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors">
                      Nachricht senden
                    </button>
                  </div>
                </form>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;